import { type FC, useState } from "react";
import { EMAIL_SENDERS } from "../../../../constants/email-senders";
import type { ProposalDraft } from "../../../../hooks/use-proposal-composer";
import { ProposalConfirmModal } from "./ProposalConfirmModal";

/**
 * @param {number} score - スコア値
 * @return {string} バッジCSSクラス
 */
function getScoreBadgeClass(score: number): string {
  if (score >= 0.8) return "badge-success";
  if (score >= 0.6) return "badge-warning";
  return "badge-error";
}

export interface ProposalComposePaneProps {
  draft: ProposalDraft;
  isGenerating: boolean;
  isSending: boolean;
  error: string | null;
  onFieldChange: (field: keyof ProposalDraft, value: string) => void;
  onSend: () => Promise<boolean>;
  onBackToResults: () => void;
  onCancelGeneration: () => void;
}

/**
 * 提案メール作成ペイン
 * @param {ProposalComposePaneProps} props
 * @return {JSX.Element}
 */
export const ProposalComposePane: FC<ProposalComposePaneProps> = ({
  draft,
  isGenerating,
  isSending,
  error,
  onFieldChange,
  onSend,
  onBackToResults,
  onCancelGeneration,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const totalPct = Math.round(draft.totalScore * 100);

  /**
   * 送信確認モーダルを開く
   * @return {void}
   */
  const handleOpenConfirm = () => {
    setShowConfirm(true);
  };

  /**
   * 送信を実行
   * @return {Promise<void>}
   */
  const handleConfirmSend = async () => {
    setShowConfirm(false);
    await onSend();
  };

  return (
    <div className="card bg-base-100 shadow-md">
      <div className="card-body space-y-3">
        {/* マッチサマリー */}
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">提案対象</span>
            <span
              className={`badge badge-sm ${getScoreBadgeClass(draft.totalScore)}`}
            >
              {totalPct}%
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-800 truncate">
            {draft.matchedSubject}
          </p>
          {draft.aiHighlights && draft.aiHighlights.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {draft.aiHighlights.map(h => (
                <span
                  key={h}
                  className="badge badge-xs badge-success badge-outline"
                >
                  {h}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* エラー表示 */}
        {error && (
          <div className="alert alert-error text-sm py-2">
            <span>{error}</span>
          </div>
        )}

        {/* 送信元 */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">送信元</span>
          </label>
          <select
            className="select select-bordered select-sm w-full"
            value={draft.fromEmail}
            onChange={e => onFieldChange("fromEmail", e.target.value)}
          >
            {EMAIL_SENDERS.map(s => (
              <option key={s.email} value={s.email}>
                {s.name} ({s.email})
              </option>
            ))}
          </select>
        </div>

        {/* 宛先 */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">宛先</span>
          </label>
          <div className="input input-bordered input-sm w-full bg-gray-50 flex items-center text-sm text-gray-600">
            {draft.toName
              ? `${draft.toName} <${draft.toEmail}>`
              : draft.toEmail}
          </div>
        </div>

        {/* 件名 */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">件名</span>
          </label>
          <input
            type="text"
            className="input input-bordered input-sm w-full"
            value={draft.subject}
            onChange={e => onFieldChange("subject", e.target.value)}
          />
        </div>

        {/* 本文 */}
        <div className="flex-1">
          <label className="label py-0">
            <span className="label-text text-xs">
              本文
              {isGenerating && (
                <span className="ml-2 text-blue-500">
                  <span className="loading loading-dots loading-xs" />{" "}
                  AI生成中...
                </span>
              )}
            </span>
          </label>
          <textarea
            className="textarea textarea-bordered w-full text-sm"
            rows={12}
            value={draft.body}
            onChange={e => onFieldChange("body", e.target.value)}
            placeholder={
              isGenerating ? "AIがメール本文を生成しています..." : ""
            }
          />
        </div>

        {/* ボタン群 */}
        <div className="flex justify-between items-center">
          <button
            className="btn btn-ghost btn-sm"
            onClick={onBackToResults}
            type="button"
          >
            ← 結果に戻る
          </button>
          <div className="flex gap-2">
            {isGenerating && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={onCancelGeneration}
                type="button"
              >
                生成停止
              </button>
            )}
            <button
              className="btn btn-primary btn-sm"
              onClick={handleOpenConfirm}
              disabled={isGenerating || isSending || !draft.body.trim()}
              type="button"
            >
              {isSending ? (
                <>
                  <span className="loading loading-spinner loading-xs" />
                  送信中...
                </>
              ) : (
                "送信"
              )}
            </button>
          </div>
        </div>

        {/* 確認モーダル */}
        <ProposalConfirmModal
          isOpen={showConfirm}
          subject={draft.subject}
          body={draft.body}
          toEmail={draft.toEmail}
          toName={draft.toName}
          fromName={draft.fromName}
          onConfirm={handleConfirmSend}
          onCancel={() => setShowConfirm(false)}
        />
      </div>
    </div>
  );
};
